import {
  PopperProps as PopperOwnProps,
  ClickAwayListener,
  Popper as MUIPopper,
  styled,
  SxProps,
  Theme,
} from '@nexelem/deprecated-theme';
import PopupState, { bindPopper, bindToggle, InjectedProps } from 'material-ui-popup-state';
import { FocusEvent, MouseEvent, ReactNode, useMemo, useRef } from 'react';
import { Placement } from './PopperTypes';

export type PopperProps = {
  readonly disabled?: boolean;
  readonly content: (popupState: InjectedProps) => ReactNode;
  readonly openingElement: ReactNode;
  readonly placement?: Placement;
  hasBorder?: boolean;
  readonly id?: string;
  readonly disablePortal?: boolean;
  additionalStyles?: SxProps;
  readonly keepOpenOnFocus?: boolean;
  readonly isOpen?: boolean;
  readonly onClick?: (popupState: ReturnType<typeof PopupState>) => (event: MouseEvent<HTMLDivElement>) => void;
};

const Content = styled('div', {
  shouldForwardProp: (prop) => prop !== 'hasBorder',
})<{ hasBorder: boolean }>(({ theme, hasBorder }) => {
  const typedTheme = theme as Theme;
  const { getColor } = typedTheme.palette;
  const { popper } = typedTheme.client.popper;
  return {
    ...typedTheme.typography.titilliumWebSemiBold12,
    ...typedTheme.palette.elevation.small,
    color: hasBorder ? getColor(popper.textColor) : getColor(popper.textColorTransparentBG),
    borderRadius: typedTheme.spacing(1),
    padding: hasBorder ? typedTheme.spacing(2) : 0,
    backgroundColor: hasBorder ? getColor(popper.tooltipBackgroundColor) : 'transparent',
  };
});

const ElementPopper = styled('div')<{ isOpen?: boolean }>(({ isOpen, theme }) => {
  const typedTheme = theme as Theme;
  const { getColor, hexToRGB } = typedTheme.palette;
  const { popper } = typedTheme.client.popper;
  return {
    display: 'inline-flex',
    '.MuiButtonBase-root': {
      backgroundColor: isOpen ? hexToRGB(getColor(popper.activeBackgroundElement), 0.9) : '',
    },
  };
});

export const Popper = ({
  content,
  openingElement,
  additionalStyles,
  id,
  hasBorder = true,
  placement = 'bottom-start',
  disablePortal = false,
  keepOpenOnFocus = false,
  disabled = false,
  onClick,
  isOpen,
}: PopperProps) => {
  const popperRef: PopperOwnProps['popperRef'] = useRef(null);

  const popperSxData = useMemo(() => {
    return {
      zIndex: 1400,
      ...additionalStyles,
    };
  }, [additionalStyles]);

  return (
    <PopupState popupId={id} variant="popper">
      {(popupState) => {
        const handleOnClick = (event: MouseEvent<HTMLDivElement>) => {
          event.stopPropagation();
          if (!disabled) {
            if (keepOpenOnFocus) {
              popupState.open(event.currentTarget);
            } else {
              popupState.toggle(event.currentTarget);
            }
          }
        };
        const handleOnBlur = (event: FocusEvent<HTMLDivElement>) => {
          const isContain = popperRef.current?.state.elements.popper.contains(event.relatedTarget);
          if (!isContain && event.relatedTarget) {
            popupState.close();
          }
        };

        const bindPropsPopper = bindPopper(popupState);
        return (
          <>
            <ElementPopper
              isOpen={popupState.isOpen}
              {...bindToggle(popupState)}
              onBlur={handleOnBlur}
              onClick={onClick ? onClick(popupState) : handleOnClick}
            >
              {openingElement}
            </ElementPopper>
            <ClickAwayListener onClickAway={() => popupState.close()}>
              <MUIPopper
                placement={placement}
                {...bindPropsPopper}
                disablePortal={disablePortal}
                open={isOpen ?? bindPropsPopper.open}
                popperRef={popperRef}
                sx={popperSxData}
              >
                <Content hasBorder={hasBorder}>{content(popupState)}</Content>
              </MUIPopper>
            </ClickAwayListener>
          </>
        );
      }}
    </PopupState>
  );
};
